import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    DateTimeSheetPopoverWidthConstantType,
    DateTimeSheetRangeSummaryConstantType,
    DateTimeSheetSingleTextConstantType,
    DateTimeSheetTimeFormatConstantType,
    DateTimeSheetRangeTextConstantType,
    DateTimeSheetTitleKeyConstantType,
    DateTimeSheetDayCountConstantType,
    DateTimeSheetSummaryConstantType,
    DateTimeSheetDynamicStyleType,
    DateTimeSheetTitleVariant
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    DateTimePickerPickerType,
    DateCalendarType
} from "../dateTimePicker/type";
import moment from "moment";
import {
    DATE_TIME_FORMAT_OPTIONS,
    formatDateTime,
    formatNumber
} from "../../helpers/locale/format";
import {
    getPopoverContentWidth
} from "../popover/components/content/stylesheet";
import {
    getDateSelectorMonthsWidth
} from "../dateSelector/util";

export const DATE_TIME_SHEET_TITLE_KEYS: Record<DateTimePickerPickerType, Record<DateTimeSheetTitleVariant, keyof NCoreUIKit.Translation>> = {
    "date-time": {
        range: "date-time-sheet-title-date-range",
        single: "date-time-sheet-title-date-time"
    },
    date: {
        range: "date-time-sheet-title-date-range",
        single: "date-time-sheet-title-date"
    },
    time: {
        range: "date-time-sheet-title-time-range",
        single: "date-time-sheet-title-time"
    }
};

export const DATE_TIME_SHEET_DAY_COUNT_KEYS: Record<"other" | "one", keyof NCoreUIKit.Translation> = {
    other: "date-time-sheet-day-count-other",
    one: "date-time-sheet-day-count-one"
};

export const getDateTimeSheetPopoverWidth = ({
    isShowWeekNumber,
    controlHeights,
    shapeConfigs,
    monthCount,
    borders,
    spaces
}: DateTimeSheetPopoverWidthConstantType): number => {
    if(monthCount === 2) {
        return getDateSelectorMonthsWidth({
            isStretched: false,
            isShowWeekNumber,
            controlHeights,
            monthCount,
            spaces
        }) + (spaces.spacingMd + borders.line) * 2 + spaces.spacingMd;
    }

    return getPopoverContentWidth({
        size: "medium",
        shapeConfigs
    }) + (isShowWeekNumber ? controlHeights.small : 0);
};

export const DATE_TIME_SHEET_CALENDAR_TYPE_KEYS: Record<DateCalendarType, keyof NCoreUIKit.Translation> = {
    month: "monthly",
    year: "yearly",
    day: "daily"
};

export const DATE_TIME_SHEET_CALENDAR_TYPES: Array<DateCalendarType> = [
    "day",
    "month",
    "year"
];

export const DATE_TIME_SHEET_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    weekday: "long",
    year: "numeric",
    day: "numeric",
    month: "long"
};

export const DATE_TIME_SHEET_RANGE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric"
};

export const DATE_TIME_SHEET_RANGE_YEAR_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric"
};

export const DATE_TIME_SHEET_RANGE_DASH = "–";

export const DATE_TIME_SHEET_RANGE_SEPARATOR = ` ${DATE_TIME_SHEET_RANGE_DASH} `;

export const DATE_TIME_SHEET_SUMMARY_SEPARATOR = " · ";

const isValidDate = (date?: Date): date is Date => {
    return date instanceof Date && !Number.isNaN(date.getTime());
};

export const getDateTimeSheetTitleKey = ({
    pickerType,
    variant
}: DateTimeSheetTitleKeyConstantType): keyof NCoreUIKit.Translation => {
    return DATE_TIME_SHEET_TITLE_KEYS[pickerType][variant === "range" ? "range" : "single"];
};

export const getDateTimeSheetTimeFormat = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds
}: DateTimeSheetTimeFormatConstantType): Intl.DateTimeFormatOptions => {
    const options: Intl.DateTimeFormatOptions = {
        ...DATE_TIME_FORMAT_OPTIONS.time,
        hourCycle: isWorkWith24HoursFormat ? "h23" : "h12"
    };

    if(isWorkWithSeconds) {
        options.second = "2-digit";
    }

    return options;
};

export const getDateTimeSheetRangeText = ({
    activeLocale,
    start,
    end
}: DateTimeSheetRangeTextConstantType): string => {
    const startText = formatDateTime({
        options: start.getFullYear() === end.getFullYear() ? DATE_TIME_SHEET_RANGE_DATE_FORMAT : DATE_TIME_SHEET_RANGE_YEAR_DATE_FORMAT,
        locale: activeLocale,
        date: start
    });

    const endText = formatDateTime({
        options: DATE_TIME_SHEET_RANGE_YEAR_DATE_FORMAT,
        locale: activeLocale,
        date: end
    });

    return `${startText}${DATE_TIME_SHEET_RANGE_SEPARATOR}${endText}`;
};

export const getDateTimeSheetDayCount = ({
    activeLocale,
    localize,
    start,
    end
}: DateTimeSheetDayCountConstantType): string => {
    const dayCount = Math.abs(moment(end).startOf("day").diff(moment(start).startOf("day"), "days")) + 1;

    return localize(DATE_TIME_SHEET_DAY_COUNT_KEYS[dayCount === 1 ? "one" : "other"], [
        formatNumber({
            locale: activeLocale,
            value: dayCount
        })
    ]);
};

export const getDateTimeSheetSingleText = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds,
    activeLocale,
    pickerType,
    date
}: DateTimeSheetSingleTextConstantType): string => {
    if(!isValidDate(date)) {
        return "";
    }

    const dateText = formatDateTime({
        options: DATE_TIME_SHEET_DATE_FORMAT,
        locale: activeLocale,
        date
    });

    const timeText = formatDateTime({
        options: getDateTimeSheetTimeFormat({
            isWorkWith24HoursFormat,
            isWorkWithSeconds
        }),
        locale: activeLocale,
        date
    });

    if(pickerType === "date") {
        return dateText;
    }

    if(pickerType === "time") {
        return timeText;
    }

    return `${dateText}${DATE_TIME_SHEET_SUMMARY_SEPARATOR}${timeText}`;
};

export const getDateTimeSheetRangeSummary = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds,
    activeLocale,
    pickerType,
    dateRange,
    localize
}: DateTimeSheetRangeSummaryConstantType): string => {
    const start = dateRange?.start;

    const end = dateRange?.end;

    const isTimeOnly = pickerType === "time";

    const boundaryFormat = isTimeOnly ? getDateTimeSheetTimeFormat({
        isWorkWith24HoursFormat,
        isWorkWithSeconds
    }) : DATE_TIME_SHEET_RANGE_YEAR_DATE_FORMAT;

    if(isValidDate(start) && isValidDate(end)) {
        if(isTimeOnly) {
            const startTimeText = formatDateTime({
                options: boundaryFormat,
                locale: activeLocale,
                date: start
            });

            const endTimeText = formatDateTime({
                options: boundaryFormat,
                locale: activeLocale,
                date: end
            });

            return `${startTimeText}${DATE_TIME_SHEET_RANGE_SEPARATOR}${endTimeText}`;
        }

        const rangeText = getDateTimeSheetRangeText({
            activeLocale,
            start,
            end
        });

        const dayCountText = getDateTimeSheetDayCount({
            activeLocale,
            localize,
            start,
            end
        });

        return `${rangeText}${DATE_TIME_SHEET_SUMMARY_SEPARATOR}${dayCountText}`;
    }

    if(isValidDate(start)) {
        return `${formatDateTime({
            options: boundaryFormat,
            locale: activeLocale,
            date: start
        })} ${DATE_TIME_SHEET_RANGE_DASH}`;
    }

    if(isValidDate(end)) {
        return `${DATE_TIME_SHEET_RANGE_DASH} ${formatDateTime({
            options: boundaryFormat,
            locale: activeLocale,
            date: end
        })}`;
    }

    return "";
};

export const getDateTimeSheetSummary = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds,
    activeLocale,
    pickerType,
    dateRange,
    localize,
    variant,
    date
}: DateTimeSheetSummaryConstantType): string => {
    if(variant === "rrule") {
        return "";
    }

    const summary = variant === "range" ? getDateTimeSheetRangeSummary({
        isWorkWith24HoursFormat,
        isWorkWithSeconds,
        activeLocale,
        pickerType,
        dateRange,
        localize
    }) : getDateTimeSheetSingleText({
        isWorkWith24HoursFormat,
        isWorkWithSeconds,
        activeLocale,
        pickerType,
        date
    });

    return summary || localize("date-time-sheet-no-selection");
};

const stylesheet = StyleSheet.create({
    footerContainer: {
        justifyContent: "flex-end",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    loadingContainer: {
        justifyContent: "center",
        alignItems: "center"
    },
    contentContainer: {
        flexDirection: "column"
    },
    clearButton: {
        marginEnd: "auto"
    }
});

export const useStyles = ({
    spaces
}: DateTimeSheetDynamicStyleType) => {
    const styles = {
        loadingContainer: {
            padding: spaces.spacingMd
        } as Mutable<ViewStyle>,
        footerContainer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        contentContainer: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
